import {
  CircleCheck,
  SquareArrowOutUpRight,
  TriangleAlert,
} from "lucide-react";
import React, { memo, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgDialog, IgLink, IgTypography } from "@intelligems/ig-theme";
import { ExperienceCategory, ThemeType } from "@intelligems/ig-types";
import { Box, CircularProgress, Grid, Skeleton } from "@mui/material";
import { useTheme } from "@mui/styles";
import {
  useGetOrg,
  useGetOrgId,
} from "@src/api/organizations/get-organization";
import { useGetThemes } from "@src/api/shopify-themes/get-themes";
import { useGetScriptStatus } from "@src/api/shops/get-script-status";
import { SelectTheme } from "@src/components/Select/SelectTheme";
import { useSelectorFactory } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { getDialog, setDialog } from "@src/store/slices/global";
import { selectedPageTargeting } from "@src/store/slices/pageTargeting";
import { getSliceSpecificRedirects } from "@src/store/slices/redirects";
import { getAuthToken } from "@src/utils";
import { copyToClipboard } from "@src/utils/copyToClipboard";
import {
  generatePreviewLink,
  getPreviewThemeId,
} from "@src/utils/generatePreviewLink";
import { FullIgScript } from "@src/utils/intelligemsThemeSnippets";
import { allIntegrationGuides } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

export interface RedirectToPluginDialogProps {
  type: ExperienceCategory;
  isLoading: boolean;
  isEditorLaunch: boolean;
}

export const RedirectToPluginDialog = memo(
  ({ type, isLoading, isEditorLaunch }: RedirectToPluginDialogProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const token = getAuthToken();
    const experience = useSelector(selectedExperience);
    const themeRedirects = useSelectorFactory(
      getSliceSpecificRedirects,
      "theme"
    );
    const pageTargeting = useSelector(selectedPageTargeting);

    const dialog = useSelector(getDialog("redirectToPluginDialog"));

    // const [shouldRenderThemePicker, setShouldRenderThemePicker] = useImmer();
    const [selectedTheme, setSelectedTheme] = useImmer<ThemeType | undefined>(
      undefined
    );

    const experienceToReference = useMemo(() => {
      if (experience) {
        return experience;
      } else return dialog.props?.experience;
    }, [experience, dialog.props?.experience]);

    const maybeThemeId = selectedTheme
      ? selectedTheme.role === "main"
        ? undefined
        : String(selectedTheme.id)
      : undefined;

    const org = useGetOrg();
    const orgId = useGetOrgId();
    const scriptStatus = useGetScriptStatus(org.data?.org?.id, maybeThemeId);

    const themes = useGetThemes();

    const loading = isLoading || !orgId;

    const handleClose = () => {
      dispatch(setDialog({ open: false, dialog: "redirectToPluginDialog" }));
      if (isEditorLaunch) {
        dispatch(setDialog({ open: true, dialog: "onsiteEditRefresh" }));
      }
    };

    useEffect(() => {
      if (themes.isSuccess) {
        if (!selectedTheme) {
          setSelectedTheme(
            themes.data.themes.find((theme) => theme.role === "main")
          );
        }
      }
    }, [themes.isSuccess]);

    const onOpen = () => {
      if (dialog.props?.id || experienceToReference?.id) {
        if (org.isSuccess) {
          window.open(
            generatePreviewLink({
              organization: org.data.org,
              experience: experienceToReference!,
              previewThemeId:
                selectedTheme?.id?.toString() ||
                getPreviewThemeId(themeRedirects),
              builderModeEntity: isEditorLaunch
                ? type === "experiment"
                  ? "experiment"
                  : "campaign"
                : undefined,
              authToken: isEditorLaunch ? token : undefined,
              pageTargeting,
            }),
            "_blank"
          );
          if (isEditorLaunch) {
            dispatch(setDialog({ open: true, dialog: "onsiteEditRefresh" }));
            dispatch(
              setDialog({ open: false, dialog: "redirectToPluginDialog" })
            );
          }
        }
      }
    };

    const subtitle = useMemo(() => {
      if (isEditorLaunch) {
        return "";
      } else {
        return `Use preview mode to verify all aspects of your
          ${type === "experiment" ? "test" : "personalization"} are working
          correctly.`;
      }
    }, [isEditorLaunch, type]);

    return (
      <IgDialog
        showDialog={dialog.isOpen}
        handleClose={handleClose}
        handleNoClick={scriptStatus.data?.exists ? undefined : () => onOpen()}
        minWidth={"500px"}
        maxWidth={"lg"}
        title={loading ? `Saving ${type}` : "Enter Preview Mode"}
        hideActions={isLoading}
        showCloseIcon={true}
        content={
          loading ? (
            <Box style={{ marginTop: 6, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container item direction={"column"} rowSpacing={2}>
              <Grid item>
                <IgTypography>{subtitle}</IgTypography>
              </Grid>
              <Grid item>
                <IgTypography>
                  Intelligems wasn't detected in your live theme. Select from
                  the dropdown below when integrating our script into your draft
                  theme.
                </IgTypography>
              </Grid>
              <Grid item>
                {themes.isLoading ? (
                  <Skeleton sx={{ width: "200px", height: "31px" }} />
                ) : (
                  <SelectTheme
                    maxWidth={"300px"}
                    value={selectedTheme ? String(selectedTheme.id) : ""}
                    onChange={(value: string) => {
                      const theme = (themes.data?.themes || []).find(
                        (t) => String(t.id) === value
                      )!;
                      setSelectedTheme(theme);
                    }}
                  />
                )}
              </Grid>
              <Grid
                container
                item
                direction={"column"}
                rowSpacing={1}
                sx={{
                  marginTop: "15px",
                  borderTop: `${theme.igBorders.core.default} solid 1px`,
                  borderBottom: `${theme.igBorders.core.default} solid 1px`,
                  padding: "10px",
                  paddingTop: "2px !important",
                  flexWrap: "nowrap",
                }}
                justifyContent={"spaceBetween"}
                alignItems={"center"}
              >
                <Grid
                  container
                  item
                  sx={{ flexWrap: "nowrap" }}
                  direction={"row"}
                  columnSpacing={2}
                  justifyContent={"spaceBetween"}
                  alignItems={"center"}
                >
                  <Grid
                    item
                    sx={{ height: scriptStatus.isLoading ? "31px" : undefined }}
                  >
                    {scriptStatus.isLoading ? (
                      <Skeleton sx={{ width: "31px", height: "31px" }} />
                    ) : scriptStatus.data?.exists ? (
                      <CircleCheck size={30} />
                    ) : (
                      <TriangleAlert
                        size={30}
                        color={theme.igIconColors.warning}
                      />
                    )}
                  </Grid>
                  <Grid item sx={{ textAlign: "left" }}>
                    {scriptStatus.isLoading ? (
                      <Skeleton sx={{ width: "237px", height: "31px" }} />
                    ) : (
                      <IgTypography
                        color={
                          scriptStatus.data?.exists
                            ? theme.palette.text.tonal.success
                            : theme.palette.text.tonal.error
                        }
                      >
                        {scriptStatus.data?.exists
                          ? "The Intelligems script is detected in this theme."
                          : "In order to use this feature you’ll need to install the Intelligems script into this theme."}
                      </IgTypography>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              {scriptStatus.data && !scriptStatus.data.exists ? (
                <Grid item>
                  <IgTypography>
                    Read our{" "}
                    <IgLink
                      onClick={() => {
                        openNewTab(allIntegrationGuides, "Integration Guides");
                      }}
                    >
                      help article
                    </IgLink>{" "}
                    for more information.
                  </IgTypography>
                </Grid>
              ) : null}
            </Grid>
          )
        }
        handleYesClick={
          scriptStatus.data && scriptStatus.data.exists
            ? () => onOpen()
            : async () => copyToClipboard(FullIgScript(orgId))
        }
        yesButtonText={
          scriptStatus.data && scriptStatus.data.exists ? (
            <span>
              <IgTypography color={"white"}>
                Continue to Selected Theme
              </IgTypography>
              <SquareArrowOutUpRight size={16} style={{ paddingLeft: "5px" }} />
            </span>
          ) : (
            "Copy Script"
          )
        }
        noButtonText={
          isLoading
            ? null
            : scriptStatus.data && scriptStatus.data.exists
              ? null
              : "Continue to Selected Theme Anyways"
        }
      />
    );
  }
);
